/* eslint-disable @typescript-eslint/no-loop-func */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useActionFolders } from "Hooks/useActionFolders";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useParams } from "react-router-dom";
import * as yup from "yup";

import BadgeSubStatus from "../../../../../components/Generic/BadgeSubStatus";
import Estimate from "../../../../../components/Generic/Estimate";
import Files from "../../../../../components/Generic/Files";
import History, { Item } from "../../../../../components/Generic/History";
import Instructions from "../../../../../components/Generic/Instructions";
import PackingMaterials from "../../../../../components/Generic/PackingMaterials";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../../../../components/Generic/UITabMaterial";
import MaintenanceTitlePage from "../../../../../components/MaintenanceTitlePage";
import { PRIVATE_ROUTES } from "../../../../../constants";
import {
  MovingResourcesProvider,
  useMovingResources,
} from "../../../../../context/MovingResources";
import { QuoteProvider } from "../../../../../context/QuoteContext";
import { useAppState } from "../../../../../context/StateContext";
import useActionAutomaticSequence from "../../../../../hooks/useActionAutomaticSequence";
import { useActionsJob } from "../../../../../hooks/useActionJob";
import { useActionQuote } from "../../../../../hooks/useActionQuote";
import { useQuoteContext } from "../../../../../hooks/useQuoteContext";
import { AutomaticSequencing } from "../../../../../models/automatic.sequencing";
import { Currency } from "../../../../../models/currency";
import { Estimate as JobEstimate } from "../../../../../models/estimate";
import { File } from "../../../../../models/file";
import { Folder } from "../../../../../models/folder";
import { Instruction } from "../../../../../models/instruction";
import { Location } from "../../../../../models/location";
import { CustomerLocations, RoomJob, TaskDate } from "../../../../../models/sales";
import { Task } from "../../../../../models/task";
import CurrencyService from "../../../../../services/currency.service";
import QuoteService from "../../../../../services/quote.service";
import {
  AutomaticSequenceOptions,
  JobSubStatus,
  Status,
  TabItem,
  TaskType,
} from "../../../../../util/enums";
import { getMeasurement } from "../../../../../util/getMeasurement";
import { getTotalCostsByPrice } from "../../../../../util/prices";
import Access from "./Access";
import Details from "./Details";
import Inventory from "./Inventory";
import LoadingSkeleton from "./skeleton";
import useStyles from "./styles";
import Summary from "./Summary";
import Tasks from "./Tasks";
import VideoSurveyTest from "./VideoSurvey";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { filterVideoSurveyFolder } from "Util/filterVideoSurvey";

type Props = {
  showTitle?: boolean;
  jobId?: number;
  className?: string;
  onCancel?: () => void;
  onSave?: () => void;
};

interface HistoryState {
  tabItem?: string;
}

const QuoteMaintenanceScreen = (props: Props) => {
  const { showTitle, jobId: jobIdProps, className, onCancel, onSave } = props;

  const { taskId: jobIdParam } = useParams<{ taskId?: string | undefined }>();

  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [summaryErrors, setSummaryErrors] = useState<yup.ValidationError[]>([]);

  const [hasError, setHasError] = useState<boolean>(false);
  const [quotePermission, setQuotePermission] = useState<{
    create: boolean;
    edit: boolean;
  }>({
    create: false,
    edit: false,
  });
  const [isEditQoute, setIsEditQoute] = useState<boolean>(false);

  const [customerOriginErrors, setCustomerOriginErrors] = useState<yup.ValidationError[]>([]);
  const [customerDestinationErrors, setCustomerDestinationErrors] = useState<yup.ValidationError[]>(
    []
  );
  const [isValidated, setIsValidated] = useState<boolean>(false);

  const jobId = jobIdParam || jobIdProps;

  const { t: translate } = useTranslation();
  const { currentUser, userPermissions } = useAppState();

  const history = useHistory<HistoryState>();

  const classes = useStyles();

  const { quote, setQuote, handleUpdateQuote, setChangedOptions, generalErrors } =
    useQuoteContext();

  const userDatePreference = currentUser?.preferences?.dateFormat;
  const userTimePreference = currentUser?.preferences?.timeFormat;

  const preferredDateFormat = useMemo(() => {
    return userDatePreference === "MM_DD_YYYY" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  }, [userDatePreference]);

  const preferredTimeFormat = useMemo(() => {
    return userTimePreference === "12_HOUR" ? "hh:mmaaa" : "HH:mm";
  }, [userTimePreference]);

  const preferredDateTimeFormat = useMemo(() => {
    return `${preferredDateFormat} ${preferredTimeFormat}`;
  }, [preferredDateFormat, preferredTimeFormat]);

  const formatDateTimeByPreference = useCallback(
    (value?: string | Date | null) => {
      if (!value) return "";

      return format(new Date(value), preferredDateTimeFormat);
    },
    [preferredDateTimeFormat]
  );

  const videoSurveyTasks: Task[] = useMemo(() => {
    return quote?.tasks?.filter((task) => task.type === TaskType.VIDEO_SURVEY && !!task.id) || [];
  }, [quote?.tasks]);

  const {
    loading: loadingResources,
    loadingDetailsResources,
    ...externalList
  } = useMovingResources();

  const {
    sendQuote,
    sendQuoteToCustomer,
    sendToCustomerLoading,
    loadingUpdateQuote,
    setHasUnsavedChanges,
    hasUnsavedChanges,
  } = useActionQuote();
  const { getAutomaticSequenceByType } = useActionAutomaticSequence();
  const { getJobHistories, getEstimates } = useActionsJob({ jobId });

  getAutomaticSequenceByType(AutomaticSequenceOptions.QUOTE, !quote?.id, setSequence);

  const { data: historyData } = getJobHistories();

  const { data: estimatesData, isFetching: isLoadingEstimates } = getEstimates();

  const { getQuoteFolders, mutateFolders } = useActionFolders({ jobId: jobIdParam });

  const { data: quoteFolderData } = getQuoteFolders();

  const [foldersFromApi, setFoldersFromApi] = useState<Folder[]>([]);
  const [quoteFolders, setQuoteFolders] = useState<Folder[]>([]);
  const [foldersDeleted, setFoldersDeleted] = useState<Folder[]>([]);
  const [filesDeleted, setFilesDeleted] = useState<Folder[]>([]);

  useEffect(() => {
    if (quoteFolderData) {
      setFoldersFromApi(quoteFolderData);
    }
  }, [quoteFolderData]);

  const normalizeFolderName = useCallback(
    (value?: string) => (value ?? "").trim().toLowerCase(),
    []
  );

  const buildRoomsFolderFromInventory = useCallback(
    (inventory: any[], jobId?: number, companyId?: number) => {
      if (!inventory?.length) return null;

      const roomSubFolders = inventory
        .filter((room) => room?.roomPictures?.length)
        .map((room) => ({
          type: "Photos & Videos",
          name: room?.name || "Room",
          isAllowedEdit: false,
          isAllowedDelete: false,
          jobId,
          companyId,
          parentFolderId: null,
          showQuantity: false,
          folders: [],
          files: (room.roomPictures || [])
            .filter((picture: any) => !!picture?.mediaLink)
            .map((picture: any, index: number) => ({
              name: picture?.name || `Photo ${index + 1}`,
              uri: picture?.mediaLink,
              type: "image",
              extension: (picture?.mediaLink?.split(".").pop() || "png").split("?")[0],
              isAllowedDelete: false,
              isAllowedEdit: false,
            })),
        }))
        .filter((roomFolder) => Array.isArray(roomFolder.files) && roomFolder.files.length > 0);

      if (!roomSubFolders.length) return null;

      return {
        type: "Photos & Videos",
        name: "Rooms",
        isAllowedEdit: false,
        isAllowedDelete: false,
        jobId,
        companyId,
        parentFolderId: null,
        showQuantity: false,
        folders: roomSubFolders,
        files: [],
      };
    },
    []
  );

  const addRoomsInsidePhotosVideosQuote = useCallback(
    (currentFolders: Folder[] = [], roomsFolder: any | null) => {
      if (!roomsFolder) return currentFolders;

      const safeFoldersList = Array.isArray(currentFolders) ? currentFolders : [];

      return safeFoldersList.map((rootFolder: any) => {
        const rootName = normalizeFolderName(rootFolder?.name);

        const isPhotosVideosRoot = rootName === "photos" || rootName === "photos & videos";
        if (!isPhotosVideosRoot) return rootFolder;

        const photosChildren = Array.isArray(rootFolder?.folders) ? rootFolder.folders : [];

        const photosChildrenUpdated = photosChildren.map((subFolder: any) => {
          const subName = normalizeFolderName(subFolder?.name);

          if (subName !== "quote") return subFolder;

          const quoteChildren = Array.isArray(subFolder?.folders) ? subFolder.folders : [];

          const alreadyHasRooms = quoteChildren.some(
            (child: any) => normalizeFolderName(child?.name) === "rooms"
          );

          if (alreadyHasRooms) return subFolder;

          return {
            ...subFolder,
            folders: [...quoteChildren, roomsFolder],
          };
        });

        return {
          ...rootFolder,
          folders: photosChildrenUpdated,
        };
      });
    },
    [normalizeFolderName]
  );

  useEffect(() => {
    const inventoryList = (quote?.inventory ?? []) as any[];

    const roomsFolderToInject = buildRoomsFolderFromInventory(
      inventoryList,
      Number(jobId),
      Number(currentUser?.companyId)
    );

    const foldersWithRooms = addRoomsInsidePhotosVideosQuote(foldersFromApi, roomsFolderToInject);

    const foldersAfterVideoSurveyFilter = filterVideoSurveyFolder(foldersWithRooms);

    setQuoteFolders(foldersAfterVideoSurveyFilter);
  }, [
    foldersFromApi,
    quote?.inventory,
    buildRoomsFolderFromInventory,
    addRoomsInsidePhotosVideosQuote,
    jobId,
    currentUser?.companyId,
  ]);

  useEffect(() => {
    if (
      quote &&
      quote?.id &&
      quote?.companyId &&
      currentUser &&
      currentUser?.companyId &&
      currentUser?.companyId !== quote?.companyId
    ) {
      history.replace(PRIVATE_ROUTES.QUOTES);
      NotificationToast.warning(translate("youAreNotAllowedToEditThisQuote"));
    }
  }, [quote]);

  useEffect(() => {
    if (isValidated) handleValidations();
  }, [quote]);

  useEffect(() => {
    if (historyData && historyData?.length) {
      setQuote((value) => ({ ...value, histories: historyData }));
    }
  }, [historyData]);

  useEffect(() => {
    if (estimatesData) {
      setQuote((value) => ({ ...value, estimate: estimatesData }));
    }
  }, [estimatesData]);

  const goBack = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace("/");
    }
  };

  const onClickCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      goBack();
    }
  };

  const { isFetching: loadingQuote } = useQuery(
    ["quotes.get"],
    () =>
      QuoteService.getById(Number(jobId), {
        isSalesApp: true,
        isNewVersion: true,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!jobId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setQuote((value) => ({ ...value, ...response.data }));
          setIsEditQoute(true);
        }
      },
    }
  );

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setQuotePermission({
        create:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_ADD_NEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_EDIT),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (sequence && !loadingQuote && !quote?.reference) {
      const value = `${sequence?.prefix ? sequence.prefix : ""}${
        sequence?.nextValue ? sequence.nextValue : ""
      }`;

      setQuote((prev) => ({
        ...prev,
        reference: value,
      }));
    }
  }, [sequence, loadingQuote]);

  const handleChangeOptions = (newOption: string, noChanges?: boolean) => {
    if (!noChanges) setHasUnsavedChanges(true);
    setChangedOptions((options) => [
      ...options.filter((option) => option.toLowerCase() !== newOption.toLowerCase()),
      newOption,
    ]);
  };

  const getItemsToHistory = () => {
    if (!jobId) {
      const firstHistoryIp = (
        quote?.histories?.find((history) => history?.userIpAddress)?.userIpAddress ?? ""
      ).trim();

      return [
        {
          title: "Creating a quote",
          activityDate: formatDateTimeByPreference(new Date()),
          activityUser: `${currentUser?.name} ${currentUser?.lastName}`,
          activityUserIp: firstHistoryIp,
        } as Item,
      ];
    }

    if (quote?.histories && quote?.histories?.length > 0) {
      const histories = quote?.histories ? [...quote.histories] : [];

      return histories.map((history) => {
        const user = history?.user ? `${history?.user?.name} ${history?.user?.lastName}` : "";
        const customer = history?.customer ? `${history?.customer?.fullName}` : "";

        return {
          title: history?.action,
          activityDate: history?.updatedAt ? formatDateTimeByPreference(history.updatedAt) : "",
          activityUser: `${user || customer}`,
          activityUserIp: history.userIpAddress ?? "",
        } as Item;
      });
    }

    return undefined;
  };

  useQuery(
    ["currencies.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id && !currencies?.length,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setCurrencies(response.data);
      },
    }
  );

  const quoteHasEstimate = (): boolean => {
    if (
      quote?.estimate?.paymentMethod &&
      quote?.estimate?.prices?.length &&
      quote?.jobSubStatus?.toLowerCase() !== JobSubStatus.WON.toLowerCase()
    ) {
      const estimatePrices = quote.estimate.prices.filter((price) => price?.groups?.length);
      let hasGroupsAndServices: boolean = false;

      if (estimatePrices.length) {
        for (const price of estimatePrices) {
          const totalCost = getTotalCostsByPrice(
            price,
            quote?.inventory || [],
            currentUser?.company?.currency,
            currencies,
            getMeasurement()
          );

          const totalIsZero = Object.values(totalCost.estimate)?.some(
            (total, value) => total + value,
            0
          );

          if (
            (!totalIsZero && totalCost.materials === 0) ||
            quote?.jobSubStatus?.toLowerCase() === JobSubStatus.ESTIMATE_APPROVED.toLowerCase()
          ) {
            hasGroupsAndServices = false;
          } else hasGroupsAndServices = true;
        }

        return hasGroupsAndServices;
      }
    }

    return false;
  };

  const checkDetailsQuoteForErrors = useMemo((): boolean => {
    const conditionsToIncludeError = ["type", "mode"];

    const hasError = generalErrors?.find((error) => {
      return conditionsToIncludeError.some((conditionError) =>
        error?.path?.includes(conditionError)
      );
    });

    return !!hasError;
  }, [generalErrors]);

  const checkAccessQuoteForErrors = useMemo((): boolean => {
    const conditionsToIncludeError = ["pickupAccess", "deliveryAccess"];

    const hasError = generalErrors?.find((error) => {
      return conditionsToIncludeError.some((conditionError) =>
        error?.path?.includes(conditionError)
      );
    });

    return !!hasError;
  }, [generalErrors]);

  const schema = yup.object().shape({
    jobTypeId: yup.number().required(translate("thisFieldIsRequired")),
    reference: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    status: yup.string().required(translate("thisFieldIsRequired")),
    consultant: yup.string().required(translate("thisFieldIsRequired")),
    mode: yup.string().required(translate("thisFieldIsRequired")),
  });

  const handleValidate = () => {
    try {
      schema.validateSync(
        {
          jobTypeId: quote?.jobTypeId,
          reference: quote?.reference,
          status: quote?.jobSubStatus,
          consultant: quote?.user?.id,
          mode: quote?.mode,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const customerSchema = yup.object().shape({
    fullName: yup.string().required(translate("thisFieldIsRequired")),
    emailCustomer: yup.string().required(translate("thisFieldIsRequired")),
    address: yup.string().required(translate("thisFieldIsRequired")),
  });

  const handleValidateCustomer = (
    type: "origin" | "destination",
    setErrors: (value: yup.ValidationError[]) => void,
    customerLocation?: CustomerLocations
  ) => {
    const customer = customerLocation?.customer;
    const location = customerLocation?.locations?.[0];

    try {
      customerSchema.validateSync(
        {
          fullName: customer?.fullName,
          emailCustomer: customer?.email,
          address: location?.longAddress,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleValidations = () => {
    const quoteIsValid = handleValidate();

    const destinationCustomerIsValid = handleValidateCustomer(
      "destination",
      setCustomerDestinationErrors,
      quote?.destination
    );

    const originCustomerIsValid = handleValidateCustomer(
      "origin",
      setCustomerOriginErrors,
      quote?.origin
    );

    const isValid = quoteIsValid && destinationCustomerIsValid && originCustomerIsValid;

    setHasError(!isValid);

    return isValid;
  };

  const { mutateAsync } = mutateFolders(
    Number(jobId),
    quoteFolders || [],
    Number(currentUser?.companyId),
    foldersDeleted,
    filesDeleted
  );

  const handleSaveQuote = () => {
    let validatedSuccessfully: boolean = true;

    if (!isValidated) {
      validatedSuccessfully = handleValidations();
    }

    setIsValidated(true);

    if (currentTab === 0) {
      if (!hasError && validatedSuccessfully) {
        sendQuote((quoteId) => {
          if (onSave) onSave();
          if (currentUser?.companyId) mutateAsync(quoteId ?? Number(jobId));
          history.push("/app/quotes");
        }, sequence);
      }
    } else {
      sendQuote((quoteId) => {
        if (onSave) onSave();
        if (currentUser?.companyId) mutateAsync(quoteId ?? Number(jobId));
        history.push("/app/quotes");
      }, sequence);
    }
  };

  const handleSaveAndSendQuote = () => {
    let validatedSuccessfully: boolean = true;

    if (!isValidated) {
      validatedSuccessfully = handleValidations();
    }

    setIsValidated(true);

    if (currentTab === 0) {
      if (!hasError && validatedSuccessfully) {
        sendQuoteToCustomer(
          (id) => {
            if (onSave) onSave();
            if (currentUser?.companyId) mutateAsync(id);
          },
          sequence,
          () => history.push("/app/quotes")
        );
      }
    } else {
      sendQuoteToCustomer(
        (id) => {
          if (onSave) onSave();
          if (currentUser?.companyId) mutateAsync(id);
        },
        sequence,
        () => history.push("/app/quotes")
      );
    }
  };

  const summarySchema = yup.object().shape({
    paymentMethod: yup.string(),
    summaryDetails: yup.boolean().required(),
    signatureCustomer: yup.string().nullable(),
    signatureConsultant: yup.string().nullable(),
  });

  const validateSummaryErrors = (): boolean => {
    try {
      summarySchema.validateSync(
        {
          paymentMethod: quote?.estimate?.paymentMethod,
          summaryDetails: quote?.summaryConfirmed,
          signatureCustomer: quote?.signatureCustomer,
          signatureConsultant: quote?.signatureConsultant,
        },
        { abortEarly: false }
      );

      setSummaryErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setSummaryErrors(error?.inner);

      return false;
    }

    if (errors.length !== 0) return false;

    return true;
  };

  const handleSaveSummary = () => {
    if (!validateSummaryErrors()) return;

    return sendQuote(onSave, sequence);
  };

  const detailsPanel: PanelProps = {
    menuItem: translate("detail"),
    key: "details",
    className: classes.uiTab,
    loading: loadingQuote || loadingDetailsResources,
    hasError:
      checkDetailsQuoteForErrors ||
      !!customerOriginErrors.length ||
      !!customerDestinationErrors.length,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Details
        customerDestinationErrors={customerDestinationErrors}
        customerOriginErrors={customerOriginErrors}
        errors={errors}
        externalList={externalList}
        isLoading={loadingResources}
        isSavingAndSending={quoteHasEstimate()}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        onSaveAndSend={handleSaveAndSendQuote}
        sequence={sequence}
        updateQuote={(attribute, value, noChanges) => {
          handleUpdateQuote(attribute, value);
          handleChangeOptions(translate("detail"), noChanges);
        }}
      />
    ),
  };

  const inventoryPanel: PanelProps = {
    menuItem: translate("inventory"),
    className: classes.uiTab,
    key: "inventory",
    loading: loadingQuote,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Inventory
        onSave={handleSaveQuote}
        onCancel={onClickCancel}
        isSavingAndSend={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
        destinations={quote?.destination?.locations || []}
        inventory={quote?.inventory}
        defaultMode={quote?.mode || ""}
        quoteJobId={quote?.jobId || -1}
        update={(roomJob: RoomJob[]) => {
          handleUpdateQuote("inventory", roomJob);
          handleChangeOptions(translate("inventory"));
        }}
        externalList={externalList}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
      />
    ),
  };

  const packingMaterialsPanel: PanelProps = {
    menuItem: translate("material"),
    key: "packingMaterial",
    className: classes.uiTab,
    loading: loadingQuote,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <PackingMaterials
        destinations={quote?.destination?.locations || []}
        inventory={quote?.inventory}
        jobId={quote?.jobId}
        handleUpdate={(roomJob: RoomJob[]) => {
          handleUpdateQuote("inventory", roomJob);
          handleChangeOptions(translate("material"));
        }}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        dataCyCancel="quote-packing-materials-button-cancel"
        dataCySave="quote-packing-materials-button-save"
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        externalList={externalList}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const tasksPanel: PanelProps = {
    menuItem: translate("date"),
    key: "tasks",
    className: classes.uiTab,
    loading: loadingQuote,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Tasks
        taskDates={quote?.taskDates}
        tasks={quote?.tasks}
        handleUpdate={(taskDates?: TaskDate[], type?: string, tasks?: Task[]) => {
          if (taskDates) handleUpdateQuote("taskDates", taskDates);
          if (type) handleUpdateQuote("type", type);
          if (tasks) handleUpdateQuote("tasks", tasks);
          handleChangeOptions(translate("date"));
        }}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        dataCyCancel="quote-tasks-button-cancel"
        dataCySave="quote-tasks-button-save"
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        externalList={externalList}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const accessPanel: PanelProps = {
    menuItem: translate("access"),
    key: "access",
    className: classes.uiTab,
    hasError: checkAccessQuoteForErrors,
    loading: loadingQuote,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Access
        data={quote}
        handleUpdate={(attribute: string, value: any) => {
          handleUpdateQuote(attribute, value);
          handleChangeOptions(translate("access"));
        }}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        errors={generalErrors}
        externalList={externalList}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const instructionsPanel: PanelProps = {
    menuItem: translate("instruction"),
    key: "instructions",
    className: classes.uiTab,
    loading: loadingQuote,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Instructions
        instructions={quote?.instructions || []}
        handleUpdate={(instructions: Instruction[]) => {
          handleUpdateQuote("instructions", instructions);
          handleChangeOptions(translate("instruction"));
        }}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        externalList={externalList}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const filesPanel: PanelProps = {
    menuItem: translate("folder"),
    key: "files",
    className: classes.uiTab,
    loading: loadingResources,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Files
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        dataCyCancel="quote-files-button-cancel"
        dataCySave="quote-files-button-save"
        folders={quoteFolders || []}
        foldersDeleted={foldersDeleted}
        filesDeleted={filesDeleted}
        inventory={quote?.inventory}
        handleUpdate={(folders?: Folder[], foldersDeleted?: Folder[], filesDeleted?: File[]) => {
          if (folders) setQuoteFolders(folders || []);
          if (foldersDeleted) setFoldersDeleted(foldersDeleted || []);
          if (filesDeleted) setFilesDeleted(filesDeleted || []);
        }}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        isSendingAndSaving={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const getEstimateProperties = (estimate?: JobEstimate): JobEstimate => {
    if (!estimate) {
      return { prices: [], paymentMethod: "" };
    }

    return {
      ...estimate,
      changed: true,
    };
  };

  const estimatePanel: PanelProps = {
    menuItem: translate("estimate"),
    className: classes.uiTab,
    key: "estimate",
    loading: loadingQuote || isLoadingEstimates,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Estimate
        isOnViewMode={quote?.jobSubStatus === JobSubStatus.WON}
        type={translate("quote")}
        orderType={quote?.jobType?.name}
        isEstimateEnableToEdit={
          quote?.jobSubStatus?.toLowerCase() === JobSubStatus.WON_DRAFT.toLowerCase()
        }
        estimate={getEstimateProperties(quote?.estimate)}
        onChangeEstimate={(estimate, hasChanged = true) => {
          handleUpdateQuote("estimate", {
            ...estimate,
            changed: hasChanged,
          });
          handleChangeOptions(translate("estimate"));
        }}
        inventory={quote?.inventory || []}
        destinationAddresses={(quote?.destination?.locations || []) as Location[]}
        taskDates={quote?.taskDates || []}
        onChangeTaskDate={(taskDates) => {
          handleUpdateQuote("taskDates", taskDates);
          handleChangeOptions(translate("date"));
        }}
        agentSelected={quote?.agent}
        onChangeAgent={(agent) => {
          if (agent && agent.id) {
            handleUpdateQuote("all", {
              ...quote,
              agentId: agent.id,
              agent,
            });
          } else {
            handleUpdateQuote("all", {
              ...quote,
              agentId: null,
              agent: null,
            });
          }
          handleChangeOptions(translate("estimate"));
        }}
        isLoading={loadingQuote}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        externalList={externalList}
        isSanvingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const videoSurveyPanel: PanelProps = {
    menuItem: translate("videoSurvey"),
    className: classes.uiTab,
    key: "videoSurvey",
    loading: loadingQuote || loadingResources,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <VideoSurveyTest
        taskId={quote?.tasks?.find((task) => task.type === TaskType.VIDEO_SURVEY)?.id}
        onCancel={onClickCancel}
        onSave={handleSaveQuote}
        tasks={quote?.tasks?.filter((task) => task.type === TaskType.VIDEO_SURVEY)}
        isUpdating={loadingUpdateQuote || sendToCustomerLoading}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const summaryPanel: PanelProps = {
    menuItem: translate("summary"),
    key: "summary",
    hasError: !!summaryErrors.length,
    className: classes.uiTab,
    loading: loadingQuote || loadingResources,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Summary
        data={quote}
        handleUpdate={(attribute: string, value: string | boolean) => {
          handleUpdateQuote(attribute, value);
          handleChangeOptions(translate("summary"));
        }}
        onChangeEstimate={(estimate, hasChanged = true) => {
          handleUpdateQuote("estimate", {
            ...estimate,
            changed: hasChanged,
          });
          handleChangeOptions(translate("estimate"));
        }}
        onCancel={onClickCancel}
        onSave={handleSaveSummary}
        isUpdating={loadingUpdateQuote}
        errors={summaryErrors}
        externalList={externalList}
        isSavingAndSending={quoteHasEstimate()}
        onSaveAndSend={handleSaveAndSendQuote}
      />
    ),
  };

  const activityPanel: PanelProps = {
    menuItem: translate("activity"),
    key: "history",
    render: () => <History items={getItemsToHistory()} isModal />,
  };

  const activityPanels = [activityPanel];

  const leftPanels = useMemo(() => {
    if (
      (isEditQoute && !isShowAction(currentUser, quotePermission?.edit)) ||
      (!isEditQoute && !isShowAction(currentUser, quotePermission?.create))
    )
      return [];

    const hasVideoSurvey = Boolean(quote?.tasks && videoSurveyTasks?.length);

    const panes = [
      detailsPanel,
      inventoryPanel,
      packingMaterialsPanel,
      hasVideoSurvey && videoSurveyPanel,
      tasksPanel,
      accessPanel,
      instructionsPanel,
      filesPanel,
      estimatePanel,
      summaryPanel,
    ].filter(Boolean);

    return panes as PanelProps[];
  }, [
    quote,
    generalErrors,
    sequence,
    loadingUpdateQuote,
    externalList,
    loadingResources,
    videoSurveyTasks,
  ]);

  const titlePage = () => {
    if (showTitle !== false) {
      const customerFullname = quote?.origin?.customer?.fullName
        ? quote?.origin?.customer?.fullName
        : "";

      const reference = quote ? quote?.reference : "";

      return (
        <MaintenanceTitlePage
          titlePage={translate("quote")}
          reference={reference}
          customerFullname={customerFullname}
          badge={
            <BadgeSubStatus
              value={quote?.jobSubStatus}
              className={classes.titleStatusBadge}
              dataCy="status"
            />
          }
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (history) {
      const { location } = history;

      if (location && location.state) {
        const { tabItem } = location.state;

        if (tabItem && tabItem.toLowerCase() === TabItem.DATE) {
          const index = leftPanels.findIndex((panel) => panel.key === TabItem.TASKS);

          setCurrentTab(index);
        } else if (tabItem && tabItem === TabItem.ESTIMATE) {
          const index = leftPanels.findIndex((panel) => panel.key === TabItem.ESTIMATE);

          setCurrentTab(index);
        }
      }
    }
  }, [history, leftPanels]);

  useEffect(() => {
    if (history?.location?.state) {
      const { tabItem } = history.location.state;

      if (tabItem === TabItem.ESTIMATE) history.replace({ ...history.location, state: undefined });
    }
  }, [currentTab]);

  return (
    <div className={className || classes.mainDiv}>
      {titlePage()}
      <Prompt when={hasUnsavedChanges} message="" />
      <UITabMaterial
        activityPanels={activityPanels}
        showActivityButton
        showActivityModal
        leftPanels={leftPanels}
        leftDataCy="quote-tabs"
        selectedLeftPanel={currentTab}
        isSendingToCustomer={sendToCustomerLoading || loadingUpdateQuote}
        onChangeLeftPanel={(_, value) => {
          setCurrentTab(Number(value));
        }}
      />
    </div>
  );
};

const QuoteMaintenance = (props: Props) => {
  return (
    <MovingResourcesProvider>
      <QuoteProvider>
        <QuoteMaintenanceScreen {...props} />
      </QuoteProvider>
    </MovingResourcesProvider>
  );
};

export default QuoteMaintenance;
